import { inject, Injectable } from '@angular/core';
import { flatten, sortBy } from 'lodash-es';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineState } from '@models/milestone-category/milestone/timeline.store';

import { CurveType, listTimelineMilestonesQuery } from '@shared/services/gql.service';
import { Option } from '@shared/types/components.type';

import { FullActivity, FullCategory, FullSettings } from '../../../state/category/category.query';
import {
  ForecastTableGridTimelineCategoriesInterface,
  ForecastTableGridTimelineItemsResultsInterface,
  ForecastTableGridTimelineMilestoneInterface,
  ForecastTableGridTimelinePhaseInterface,
} from '../models/forecast-table-grid.model';

@Injectable({
  providedIn: 'root',
})
export class ForecastTableGridPeriodService {
  private timelineQuery = inject(TimelineQuery);

  PeriodPatientOptions: Option<string>[] = [
    {
      label: 'Patient Curves',
      value: 'Patient Curves',
    },
  ];
  PeriodSiteOptions: Option<CurveType>[] = [
    {
      label: 'Net Sites',
      value: CurveType.NET,
    },
    {
      label: 'Sites Activated',
      value: CurveType.ACTIVATION,
    },
    {
      label: 'Sites Closed',
      value: CurveType.CLOSE_OUT,
    },
  ];

  getTimelineCategories(milestones: TimelineState): ForecastTableGridTimelineCategoriesInterface {
    const { items } = milestones;

    return items.reduce(
      (
        obj: ForecastTableGridTimelineCategoriesInterface,
        timelineMilestone: listTimelineMilestonesQuery
      ) => {
        const milestoneObj = obj;

        // Initialize object
        milestoneObj[timelineMilestone.milestone.milestone_category_id] ||= {
          milestones: [],
          name: '',
        };
        // Initialize name
        milestoneObj[timelineMilestone.milestone.milestone_category_id].name =
          timelineMilestone.milestone.milestone_category.name;
        // Initialze dates
        milestoneObj[timelineMilestone.milestone.milestone_category_id].milestones.push({
          ...timelineMilestone.milestone,
          end_date:
            timelineMilestone.actual_end_date ||
            timelineMilestone.revised_end_date ||
            timelineMilestone.contract_end_date,
          start_date:
            timelineMilestone.actual_start_date ||
            timelineMilestone.revised_start_date ||
            timelineMilestone.contract_start_date,
        });

        return milestoneObj;
      },
      {}
    );
  }

  getTimelineMilestones(
    phases: ForecastTableGridTimelinePhaseInterface[]
  ): ForecastTableGridTimelineMilestoneInterface[] {
    const filteredPhases = phases.filter((phase) => {
      return phase.name !== 'Custom' && phase.cat_id !== '';
    });

    return sortBy(flatten([...filteredPhases.map((phase) => phase.milestones)]), [
      'start_date',
      'end_date',
      'name',
    ]);
  }

  getTimelinePhases(): ForecastTableGridTimelinePhaseInterface[] {
    const milestoneResults = this.timelineQuery.getValue();
    const categories = this.getTimelineCategories(milestoneResults);

    const phases: ForecastTableGridTimelinePhaseInterface[] = Object.entries(categories)
      .filter(([, timelineCategory]) => timelineCategory.milestones.length >= 1)
      .map(([cat_id, { milestones, name }]) => {
        return {
          cat_id,
          firstMilestone: milestones[0],
          lastMilestone: milestones[milestones.length - 1],
          milestones,
          name,
        } as ForecastTableGridTimelinePhaseInterface;
      })
      .filter((phase) => phase.milestones.every((milestone) => !!milestone));

    phases.push({
      cat_id: '',
      firstMilestone: null,
      lastMilestone: null,
      milestones: [],
      name: 'Custom',
    });

    return phases;
  }

  isCustomPeriodFilled(primarySettings: FullSettings): boolean {
    return (
      !primarySettings.milestone_category &&
      !!(primarySettings.start_milestone || primarySettings.period_start_date) &&
      !!(primarySettings.end_milestone || primarySettings.period_end_date)
    );
  }

  parseCategoryPeriod(category: FullActivity | FullCategory): string {
    return category.primary_settings.milestone_category?.name || '';
  }

  parsePhases(
    timelinePhases: ForecastTableGridTimelinePhaseInterface[],
    fullSettings?: FullSettings
  ): Option<string>[] {
    if (!fullSettings || (fullSettings && fullSettings.milestone_category)) {
      return timelinePhases.map((phase) => {
        return {
          label: phase.name !== 'Custom' ? `Preset: ${phase.name}` : `${phase.name}`,
          value: phase.cat_id,
        };
      });
    }

    if (!fullSettings.milestone_category) {
      const startDate = fullSettings.start_milestone?.name || fullSettings.period_start_date;
      const endDate = fullSettings.end_milestone?.name || fullSettings.period_end_date;

      return timelinePhases.map((phase) => {
        return {
          label:
            phase.name !== 'Custom'
              ? `Preset: ${phase.name}`
              : `${phase.name}: ${startDate || ''} - ${endDate || ''}`,
          value: phase.cat_id,
        };
      });
    }

    return [];
  }

  parseSelectedPhase(
    timelinePhases: ForecastTableGridTimelinePhaseInterface[],
    fullSettings?: FullSettings
  ): string {
    if (!fullSettings) {
      return '';
    }

    if (fullSettings.milestone_category) {
      return (
        timelinePhases.find((phase) => {
          return phase.cat_id === fullSettings.milestone_category?.id;
        })?.cat_id || ''
      );
    }

    if (!fullSettings.milestone_category) {
      return (
        timelinePhases.find((phase) => {
          return phase.name === 'Custom';
        })?.cat_id || ''
      );
    }

    return '';
  }

  parseTimelineItems(
    timelineItems: ForecastTableGridTimelineItemsResultsInterface | never[]
  ): [ForecastTableGridTimelinePhaseInterface[], ForecastTableGridTimelineMilestoneInterface[]] {
    if (!timelineItems) {
      return [[], []];
    }

    const timelinePhases = this.getTimelinePhases();
    const timelineMilestones = this.getTimelineMilestones(timelinePhases);

    return [timelinePhases, timelineMilestones];
  }
}
